import React from 'react';
import { Car, Bike, Info } from 'lucide-react';
import { DriverInfo } from './types';
import ProfileSearch from './ProfileSearch';
import { ProfileSearchResult } from './types';

interface DriverInfoStepProps {
  vehicleType: 'auto' | 'motorcycle';
  driverInfo: DriverInfo;
  setDriverInfo: (info: DriverInfo) => void;
  searchDriverName: string;
  setSearchDriverName: (value: string) => void;
  searchRaceNumber: string;
  setSearchRaceNumber: (value: string) => void;
  isSearching: boolean;
  searchResults: ProfileSearchResult[];
  raceNumberError: string | null;
  onSearchDriverName: () => void;
  onSearchRaceNumber: () => void;
  onSelectSearchResult: (result: ProfileSearchResult) => void;
}

export default function DriverInfoStep({
  vehicleType,
  driverInfo,
  setDriverInfo,
  searchDriverName,
  setSearchDriverName,
  searchRaceNumber,
  setSearchRaceNumber,
  isSearching,
  searchResults,
  raceNumberError,
  onSearchDriverName,
  onSearchRaceNumber,
  onSelectSearchResult
}: DriverInfoStepProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        {vehicleType === 'auto' ? (
          <Car className="h-8 w-8 text-primary-500" />
        ) : (
          <Bike className="h-8 w-8 text-primary-500" />
        )}
        <h3 className="text-xl font-semibold text-white">
          {vehicleType === 'auto' ? 'Auto' : 'Motorcycle'} Driver Information
        </h3>
      </div>

      <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-blue-300 text-sm font-medium">About This Section</p>
            <p className="text-blue-200 text-sm mt-1">
              This section is for the <strong>driver's information</strong>. Please fill in all required fields marked with *.
              This information will be used for event registrations and official documentation.
            </p>
          </div>
        </div>
      </div>

      {/* Profile Search */}
      <ProfileSearch
        searchDriverName={searchDriverName}
        setSearchDriverName={setSearchDriverName}
        searchRaceNumber={searchRaceNumber}
        setSearchRaceNumber={setSearchRaceNumber}
        isSearching={isSearching}
        searchResults={searchResults}
        raceNumberError={raceNumberError}
        onSearchDriverName={onSearchDriverName}
        onSearchRaceNumber={onSearchRaceNumber}
        onSelectSearchResult={onSelectSearchResult}
      />

      {/* Driver Information Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {vehicleType === 'motorcycle' ? "Driver's Name" : 'Name of Driver'} *
          </label>
          <input
            type="text"
            value={driverInfo.driverName}
            onChange={(e) => setDriverInfo({ ...driverInfo, driverName: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Nickname
          </label>
          <input
            type="text"
            value={driverInfo.nickname}
            onChange={(e) => setDriverInfo({ ...driverInfo, nickname: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Address *
          </label>
          <input
            type="text"
            value={driverInfo.address}
            onChange={(e) => setDriverInfo({ ...driverInfo, address: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            ID Number *
          </label>
          <input
            type="text"
            value={driverInfo.idNumber}
            onChange={(e) => setDriverInfo({ ...driverInfo, idNumber: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Date of Birth *
          </label>
          <input
            type="date"
            value={driverInfo.dateOfBirth}
            onChange={(e) => setDriverInfo({ ...driverInfo, dateOfBirth: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Nationality
          </label>
          <input
            type="text"
            value={driverInfo.nationality}
            onChange={(e) => setDriverInfo({ ...driverInfo, nationality: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Country
          </label>
          <input
            type="text"
            value={driverInfo.country}
            onChange={(e) => setDriverInfo({ ...driverInfo, country: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Mobile Phone *
          </label>
          <input
            type="tel"
            value={driverInfo.mobilePhone}
            onChange={(e) => setDriverInfo({ ...driverInfo, mobilePhone: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Driver's License Number
          </label>
          <input
            type="text"
            value={driverInfo.licenseNumber}
            onChange={(e) => setDriverInfo({ ...driverInfo, licenseNumber: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {vehicleType === 'motorcycle' ? 'License Expiration Date' : 'Expiration Date'}
          </label>
          <input
            type="date"
            value={driverInfo.expirationDate}
            onChange={(e) => setDriverInfo({ ...driverInfo, expirationDate: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Team Name
          </label>
          <input
            type="text"
            value={driverInfo.teamName}
            onChange={(e) => setDriverInfo({ ...driverInfo, teamName: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Club Name
          </label>
          <input
            type="text"
            value={driverInfo.clubName}
            onChange={(e) => setDriverInfo({ ...driverInfo, clubName: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={driverInfo.email}
            onChange={(e) => setDriverInfo({ ...driverInfo, email: e.target.value })}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-3 text-white focus:ring-2 focus:ring-primary-500 focus:outline-none"
            disabled
          />
        </div>
      </div>
    </div>
  );
} 